import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { money } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { breadcrumbSchema } from "@/lib/seo";

const AMOUNTS = [
  { lookup_key: "gift_card_25", amount: 2500 },
  { lookup_key: "gift_card_50", amount: 5000 },
  { lookup_key: "gift_card_100", amount: 10000 },
  { lookup_key: "gift_card_150", amount: 15000 },
];

const inputCls =
  "w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600";

export default function GiftCards() {
  const [selected, setSelected] = useState(AMOUNTS[1]);
  const [form, setForm] = useState({ recipient_name: "", recipient_email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const buy = async (e) => {
    e.preventDefault();
    if (!form.recipient_name.trim() || !form.recipient_email.trim()) {
      toast.error("Add the recipient's name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/giftcards/checkout", {
        lookup_key: selected.lookup_key,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout — please try again.");
      setLoading(false);
    }
  };

  return (
    <main data-testid="giftcards-page" className="pt-40 pb-32 min-h-screen">
      <Seo
        title="Digital Gift Cards for Custom Engraving | Ember & Steel Studio"
        description="Give a digital gift card for personalised laser engraving and UV printing from Ember & Steel Studio in Leduc, Alberta. Delivered by email, redeemable on any shop product."
        path="/gift-cards"
        jsonLd={breadcrumbSchema([["Home", "/"], ["Gift Cards", "/gift-cards"]])}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Gift Cards</span>
            <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight leading-tight">
              Give them<br />the choice
            </h1>
            <p className="text-zinc-400 mt-8 leading-relaxed max-w-md">
              A digital gift card, delivered by email within minutes of payment with a unique
              code and your personal message. They pick the piece — we make it theirs.
            </p>
            <div className="mt-12 space-y-4 font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500">
              <p>01 — Pick an amount</p>
              <p>02 — We email their card instantly</p>
              <p>03 — They redeem the code at checkout</p>
            </div>
            <div className="mt-12 relative border border-zinc-800 bg-[#0C0C0C] p-8 max-w-sm overflow-hidden">
              <div className="absolute inset-0 spotlight" />
              <Gift size={22} className="text-[#C87455]" strokeWidth={1.5} />
              <p className="font-serif-display text-2xl text-white mt-4">
                <span className="text-[#C87455]">Ember</span> &amp; <span className="text-[#A9B0B7]">Steel</span> Gift Card
              </p>
              <p className="font-mono-tech text-xs text-zinc-500 mt-2 tracking-[0.25em]">ES-XXXX-XXXX</p>
              <p className="font-serif-display text-3xl text-[#C87455] mt-6">{money(selected.amount)}</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={buy} className="bg-[#0A0A0A] border border-zinc-900 p-8 md:p-12 space-y-10" data-testid="giftcard-form">
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-4">Amount (CAD)</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {AMOUNTS.map((a) => (
                    <button
                      key={a.lookup_key}
                      type="button"
                      data-testid={`giftcard-amount-${a.amount / 100}`}
                      onClick={() => setSelected(a)}
                      className={`py-5 border font-serif-display text-2xl transition-colors ${
                        selected.lookup_key === a.lookup_key
                          ? "border-[#C87455] text-[#C87455] bg-[#C87455]/5"
                          : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                      }`}
                    >
                      {money(a.amount).replace(".00", "")}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Recipient name *</label>
                  <input data-testid="giftcard-recipient-name" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className={inputCls} placeholder="Their name" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Recipient email *</label>
                  <input data-testid="giftcard-recipient-email" type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className={inputCls} placeholder="them@email.com" />
                </div>
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Personal message (optional)</label>
                <textarea
                  data-testid="giftcard-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, 300) })}
                  rows={3}
                  className={`${inputCls} resize-none`}
                  placeholder="Happy birthday! Pick something that deserves your name on it…"
                />
              </div>
              <button
                data-testid="giftcard-buy-btn"
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-10 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm flex items-center justify-center gap-3 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <>Buy {money(selected.amount)} Gift Card <ArrowRight size={14} /></>}
              </button>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                Delivered by email after payment · Never expires · Redeemable at checkout
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
